import { useState, useEffect, useRef } from 'react'

interface ForeignAmountModificationModalProps {
  isOpen: boolean
  calculatedAmount: number
  currencyCode: string
  minUnit: number
  onConfirm: (newAmount: number) => void
  onCancel: () => void
}

function ForeignAmountModificationModal({
  isOpen,
  calculatedAmount,
  currencyCode,
  minUnit,
  onConfirm,
  onCancel,
}: ForeignAmountModificationModalProps) {
  const [newAmount, setNewAmount] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const amountInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setNewAmount(calculatedAmount.toString())
      setError(null)
      setTimeout(() => {
        amountInputRef.current?.focus()
        amountInputRef.current?.select()
      }, 100)
    }
  }, [isOpen, calculatedAmount])

  if (!isOpen) return null

  const handleConfirm = () => {
    const amountNum = parseFloat(newAmount)

    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setError('유효한 금액을 입력해주세요.')
      return
    }

    if (amountNum % minUnit !== 0) {
      setError(`${minUnit} 단위로 입력해주세요.`)
      return
    }

    onConfirm(amountNum)
  }

  const handleCancel = () => {
    setNewAmount('')
    setError(null)
    onCancel()
  }

  const handleReset = () => {
    setNewAmount(calculatedAmount.toString())
    setError(null)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-fx-text-dark">외화 금액 수정</h2>
          <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">계산된 금액 ({currencyCode})</label>
            <div className="px-4 py-3 bg-gray-100 rounded-lg text-lg font-semibold text-fx-text-dark">
              {calculatedAmount.toLocaleString()} {currencyCode}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">적용할 금액 ({minUnit} 단위)</label>
            <input
              ref={amountInputRef}
              type="number"
              value={newAmount}
              onChange={(e) => {
                setNewAmount(e.target.value)
                setError(null)
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fx-sell focus:border-transparent text-lg"
              placeholder="0"
              step={minUnit}
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-2 rounded-lg text-sm">
            수정된 외화 금액에 맞춰 실제 원화 비용과 거스름돈이 재계산됩니다.
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={handleReset}
            className="px-6 py-2 border border-gray-300 rounded-lg text-fx-text-dark font-medium hover:bg-gray-50 transition-all"
          >
            초기화
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-fx-confirm text-white rounded-lg font-medium hover:opacity-90 transition-all"
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  )
}

export default ForeignAmountModificationModal
